import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  ACCESS_POLICY_DECISIONS,
  type AccessPolicyDecision,
} from "../shared/constants";
import type {
  CloudflareAccessApplication,
  CloudflareAccessPolicy,
} from "../shared/types";
import { usePatchDraft } from "./admin-model";
import { useAdminApi } from "./admin-query";
import { errorMessage } from "./api";

const DECISION_LABELS: Record<AccessPolicyDecision, string> = {
  allow: "Allow",
  deny: "Block",
  non_identity: "Non-identity",
  bypass: "Bypass",
  service_auth: "Service Auth",
};

interface PolicyDraft {
  name: string;
  // Carries an unmodelled decision through unchanged. The select offers no
  // such option and saving is blocked until an operator picks a real one, so
  // an edit can neither silently rewrite nor silently keep it.
  decision: AccessPolicyDecision | "unknown";
  precedence: number;
  sessionDuration: string;
  include: string;
  require: string;
  exclude: string;
  justification: boolean;
  prompt: string;
  temporary: boolean;
  approvalGroups: string;
}

function formatted(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function initialDraft(
  application: CloudflareAccessApplication,
  policy?: CloudflareAccessPolicy,
): PolicyDraft {
  return {
    name: policy?.name ?? "",
    decision: policy?.decision ?? "allow",
    precedence:
      policy?.precedence ??
      Math.max(0, ...application.policies.map((item) => item.precedence)) + 1,
    sessionDuration: policy?.sessionDuration ?? "",
    include: formatted(policy?.include ?? [{ everyone: {} }]),
    require: formatted(policy?.require ?? []),
    exclude: formatted(policy?.exclude ?? []),
    justification: policy?.purposeJustificationRequired ?? false,
    prompt: policy?.purposeJustificationPrompt ?? "",
    temporary: policy?.temporaryAuthentication ?? false,
    approvalGroups: formatted(policy?.approvalGroups ?? []),
  };
}

function parseArray(
  value: string,
  label: string,
): Array<Record<string, unknown>> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error(`${label} is not valid JSON.`);
  }
  if (!Array.isArray(parsed)) throw new Error(`${label} must be a JSON array.`);
  return parsed as Array<Record<string, unknown>>;
}

export function CloudflarePolicyEditor({
  application,
  policy,
  onCancel,
  onDone,
}: {
  application: CloudflareAccessApplication;
  policy?: CloudflareAccessPolicy;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const adminApi = useAdminApi();
  const [draft, patchDraft] = usePatchDraft(() =>
    initialDraft(application, policy),
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const base = `/api/admin/cloudflare/applications/${encodeURIComponent(application.id)}/policies`;

  const saveMutation = useMutation({
    // Async so the field-level JSON errors from `parseArray` surface as
    // mutation errors instead of throwing out of the submit handler.
    mutationFn: async () => {
      if (draft.decision === "unknown") {
        throw new Error(
          "This policy uses a decision this console does not recognize. Choose a decision before saving.",
        );
      }
      return adminApi(
        policy ? `${base}/${encodeURIComponent(policy.id)}` : base,
        {
          method: policy ? "PUT" : "POST",
          body: JSON.stringify({
            confirmation: policy?.id ?? application.id,
            name: draft.name.trim(),
            decision: draft.decision,
            precedence: draft.precedence,
            include: parseArray(draft.include, "Include rules"),
            require: parseArray(draft.require, "Require rules"),
            exclude: parseArray(draft.exclude, "Exclude rules"),
            sessionDuration: draft.sessionDuration.trim(),
            purposeJustificationRequired: draft.justification,
            purposeJustificationPrompt: draft.prompt.trim(),
            approvalRequired: draft.temporary,
            approvalGroups: parseArray(draft.approvalGroups, "Approval groups"),
          }),
        },
      );
    },
    onSuccess: onDone,
  });

  const deleteMutation = useMutation({
    mutationFn: (target: CloudflareAccessPolicy) =>
      adminApi(`${base}/${encodeURIComponent(target.id)}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmation: target.id }),
      }),
    onSuccess: onDone,
  });

  const busy = saveMutation.isPending || deleteMutation.isPending;
  const error =
    (saveMutation.error &&
      errorMessage(saveMutation.error, "The policy could not be saved.")) ||
    (deleteMutation.error &&
      errorMessage(deleteMutation.error, "The policy could not be deleted.")) ||
    "";

  function submit(event: FormEvent) {
    event.preventDefault();
    saveMutation.mutate();
  }

  if (deleteConfirmation && policy) {
    return (
      <div className="access-policy-confirm">
        <div className="notice notice--error">
          <strong>Delete {policy.name}?</strong>
          This can immediately change who can reach {application.name}.
        </div>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="dialog__actions dialog__actions--inline">
          <button
            className="button button--secondary"
            disabled={busy}
            onClick={() => setDeleteConfirmation(false)}
            type="button"
          >
            Keep policy
          </button>
          <button
            className="button button--danger"
            disabled={busy}
            onClick={() => deleteMutation.mutate(policy)}
            type="button"
          >
            {busy ? "Deleting…" : "Delete policy"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="dialog__form" onSubmit={submit}>
      <div className="dialog__body access-policy-form">
        <div className="form-grid">
          <label>
            <span>Policy name</span>
            <input
              data-autofocus
              disabled={busy}
              maxLength={120}
              onChange={(event) => patchDraft({ name: event.target.value })}
              required
              value={draft.name}
            />
          </label>
          <label>
            <span>Decision</span>
            <select
              disabled={busy}
              onChange={(event) =>
                patchDraft({
                  decision: event.target.value as AccessPolicyDecision,
                })
              }
              value={draft.decision}
            >
              {draft.decision === "unknown" && (
                <option disabled value="unknown">
                  Unrecognized — choose a decision
                </option>
              )}
              {ACCESS_POLICY_DECISIONS.map((decision) => (
                <option key={decision} value={decision}>
                  {DECISION_LABELS[decision]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Precedence</span>
            <input
              disabled={busy}
              min={1}
              onChange={(event) =>
                patchDraft({ precedence: Number(event.target.value) })
              }
              required
              type="number"
              value={draft.precedence}
            />
          </label>
          <label>
            <span>Session duration</span>
            <input
              disabled={busy}
              onChange={(event) =>
                patchDraft({ sessionDuration: event.target.value })
              }
              placeholder="Same as application (blank)"
              value={draft.sessionDuration}
            />
          </label>
        </div>
        <div className="policy-json-grid">
          <label>
            <span>Include rules (OR)</span>
            <textarea
              disabled={busy}
              onChange={(event) => patchDraft({ include: event.target.value })}
              spellCheck={false}
              value={draft.include}
            />
          </label>
          <label>
            <span>Require rules (AND)</span>
            <textarea
              disabled={busy}
              onChange={(event) => patchDraft({ require: event.target.value })}
              spellCheck={false}
              value={draft.require}
            />
          </label>
          <label>
            <span>Exclude rules (NOT)</span>
            <textarea
              disabled={busy}
              onChange={(event) => patchDraft({ exclude: event.target.value })}
              spellCheck={false}
              value={draft.exclude}
            />
          </label>
        </div>
        <label className="checkbox-line">
          <input
            checked={draft.justification}
            disabled={busy}
            onChange={(event) =>
              patchDraft({ justification: event.target.checked })
            }
            type="checkbox"
          />
          <span>Require purpose justification</span>
        </label>
        {draft.justification && (
          <label>
            <span>Purpose prompt</span>
            <input
              disabled={busy}
              maxLength={500}
              onChange={(event) => patchDraft({ prompt: event.target.value })}
              value={draft.prompt}
            />
          </label>
        )}
        <label className="checkbox-line">
          <input
            checked={draft.temporary}
            disabled={busy}
            onChange={(event) =>
              patchDraft({ temporary: event.target.checked })
            }
            type="checkbox"
          />
          <span>Temporary authentication approval</span>
        </label>
        {draft.temporary && (
          <label>
            <span>Approval groups (JSON)</span>
            <textarea
              disabled={busy}
              onChange={(event) =>
                patchDraft({ approvalGroups: event.target.value })
              }
              spellCheck={false}
              value={draft.approvalGroups}
            />
            <small>
              Example: [&#123;&quot;approvalsNeeded&quot;:1,
              &quot;emailAddresses&quot;:[&quot;admin@example.com&quot;]&#125;]
            </small>
          </label>
        )}
        {error && <div className="notice notice--error">{error}</div>}
      </div>
      <footer className="dialog__actions">
        {policy && (
          <button
            className="button button--danger-quiet dialog__danger-action"
            disabled={busy}
            onClick={() => setDeleteConfirmation(true)}
            type="button"
          >
            Delete policy
          </button>
        )}
        <button
          className="button button--secondary"
          disabled={busy}
          onClick={onCancel}
          type="button"
        >
          Back
        </button>
        <button
          className="button button--primary"
          disabled={busy}
          type="submit"
        >
          {busy ? "Saving…" : policy ? "Save policy" : "Create policy"}
        </button>
      </footer>
    </form>
  );
}
